import React from "react";
import { Box, Typography, Grid, Paper, LinearProgress } from "@mui/material";
import { useExerciseCache } from "../../utils/ExerciseCache";
import useCoachConsoleDocumentIdsVisible from "../../hooks/useCoachConsoleDocumentIdsVisible";

/**
 * Component to display exercise cache statistics and performance metrics
 */
const ExerciseCacheStats = ({ performanceInfo }) => {
  const showDocumentIds = useCoachConsoleDocumentIdsVisible();
  const { getStats } = useExerciseCache();
  const [cacheStats, setCacheStats] = React.useState(getStats());

  // Update stats periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCacheStats(getStats());
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []); // Empty dependency array since getStats shouldn't change

  const cacheUtilization =
    (cacheStats.cacheSize / cacheStats.maxCacheSize) * 100;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Exercise Loading Performance
      </Typography>

      <Grid container spacing={2}>
        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Loading Performance
            </Typography>

            {performanceInfo && (
              <Box>
                <Typography variant="body2">
                  Total Exercises: {performanceInfo.totalExercises || 0}
                </Typography>
                <Typography variant="body2">
                  Filtered: {performanceInfo.filteredExercises || 0}
                </Typography>
                <Typography variant="body2">
                  Displayed: {performanceInfo.displayedExercises || 0}
                </Typography>
                {performanceInfo.totalPages && (
                  <Typography variant="body2">
                    Page {performanceInfo.currentPage || 1} of{" "}
                    {performanceInfo.totalPages}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Cache Statistics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Cache Performance
            </Typography>

            <Typography variant="body2">
              Cache Size: {cacheStats.cacheSize} / {cacheStats.maxCacheSize}
            </Typography>

            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={cacheUtilization}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                {cacheUtilization.toFixed(1)}% utilized
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mt: 1 }}>
              Total Tracked: {cacheStats.totalTrackedExercises}
            </Typography>

            <Typography variant="body2">
              Total Accesses: {cacheStats.totalAccesses}
            </Typography>

            <Typography variant="body2">
              Frequent Exercises: {cacheStats.frequentExercisesCount}
            </Typography>

            <Typography variant="body2">
              Cache Hit Rate: {cacheStats.cacheHitRate.toFixed(1)}%
            </Typography>
          </Paper>
        </Grid>

        {/* Top Frequently Accessed Exercises */}
        {cacheStats.topFrequentExercises.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Top Frequently Accessed Exercises
              </Typography>

              <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                {cacheStats.topFrequentExercises
                  .slice(0, 10)
                  .map((exerciseId, index) => (
                    <Typography
                      key={exerciseId}
                      variant="body2"
                      sx={{ py: 0.5 }}
                    >
                      {index + 1}.{" "}
                      {showDocumentIds
                        ? `Exercise ID: ${exerciseId}`
                        : "Exercise ID: (hidden)"}
                    </Typography>
                  ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ExerciseCacheStats;
